import type { FC } from "react";
import { Card, CardRowSimple, ColoredLabel, formatDateTimeFromISO, LoadingSpinner } from "widgets";

import { getCssColor } from "@/app-utils/color.ts";
import { useError } from "@/app-utils/graphql/useError.js";
import { SelMessage } from "@/messages/SelMessage.js";
import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { getUserDisplayName } from "@/modules/users/utils/user.js";
import { getProjectLabel } from "@/redux/modules/config.js";
import { SampleLockedIcon } from "@/ui-modules/core/tiles/components/SampleTile.tsx";
import { MinimalSampleTile } from "@/ui-modules/entities/samples/components/MinimalSampleTile.js";
import type { MinimalSampleTileFragment } from "@/ui-modules/entities/samples/graphql/sample-fragments.gql.ts";
import { useGetSampleInfoQuery } from "@/ui-modules/entities/samples/graphql/samples-queries.gql.js";

import type { Sample } from "../utils/types.js";

interface SampleCardProps {
  /** If id=0 => hidden: true */
  id?: number | null;
  /** Restricted content, genome.sample.hidden (from graphql) */
  hidden?: boolean | null;
  /** Don't show the Tile for the "Name" section */
  isEntitySummary?: boolean;
  /** Card title */
  title?: string;
}

export const SampleCard: FC<SampleCardProps> = (props) => {
  const restricted = props.hidden || props.id === 0;
  const { data, loading, error } = useGetSampleInfoQuery({
    variables: { sampleId: props.id ?? 0 },
    skip: restricted || props.id == undefined,
  });
  void useError(error);

  const sample = data?.sample?.sample;

  return (
    <Card title={props.title ?? "Sample Info"}>
      {loading && !sample ? (
        <LoadingSpinner />
      ) : (
        <>
          {restricted ? (
            // No data loaded because skipped, so we fake a hidden sample
            <CardRowSimple label={"Name"}>
              <MinimalSampleTile sample={{ id: 0, hidden: true } as Sample} />
            </CardRowSimple>
          ) : !sample ? (
            // nothing loaded but no specific case
            <SelMessage id={"noContent.samples"} />
          ) : sample.hidden ? (
            // The sample is loaded but hidden. restricted is set in any parent only if needed
            <CardRowSimple label={"Name"}>
              <MinimalSampleTile sample={sample as unknown as MinimalSampleTileFragment} />
            </CardRowSimple>
          ) : (
            <>
              <CardRowSimple label={"Name"}>
                {props.isEntitySummary ? sample.name : <MinimalSampleTile sample={sample} />}
              </CardRowSimple>
              <CardRowSimple label={getProjectLabel(true, false)}>
                {sample.project ? (
                  <ProjectColoredLink project={sample.project} />
                ) : (
                  <>You do not have access to this {getProjectLabel(false, false)}.</>
                )}
              </CardRowSimple>
              <CardRowSimple label={"Type"}>{sample.type?.name}</CardRowSimple>
              <CardRowSimple label={"Description"}>{sample.description}</CardRowSimple>
              {sample.created && <CardRowSimple label={"Added"}>{formatDateTimeFromISO(sample.created)}</CardRowSimple>}
              {sample.status && (
                <CardRowSimple label={"Status"}>
                  <ColoredLabel
                    color={sample.status.color ? getCssColor(sample.status.color) : undefined}
                    label={sample.status.name ?? ""}
                  />
                </CardRowSimple>
              )}
              {sample.modified && (
                <CardRowSimple label={"Date modified"}> {formatDateTimeFromISO(sample.modified)}</CardRowSimple>
              )}
              {sample.submitter && (
                <CardRowSimple label={"Submitter"}> {getUserDisplayName(sample.submitter, false, false)}</CardRowSimple>
              )}
              {sample.receiver && (
                <CardRowSimple label={"Receiver"}> {getUserDisplayName(sample.receiver, false, false)} </CardRowSimple>
              )}
              {sample.locked && (
                <CardRowSimple label={"Locked"}>
                  <SampleLockedIcon size={20} />
                </CardRowSimple>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
};
